import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, BlockStack, Button, Card, IndexTable, InlineStack, Select, Text, TextField } from "@shopify/polaris";
import { useNavigate, useOutletContext } from "react-router-dom";
import agent from "../../app/api/agent";
import { formatLocalDateTime } from "./utils/dateTime";
import { AdminShopifyAppInstallMapping, AdminStoreSummary } from "../../app/models/Admin";

type AdminConsoleContext = {
  token: string;
  onUnauthorized: () => void;
};

const emptyInstallMapping: AdminShopifyAppInstallMapping = {
  shopDomain: "",
  appName: "",
  clientId: "",
  secretKey: "",
  appHandle: "",
};

export default function AdminStoresPage() {
  type ActiveFilter = "all" | "active" | "inactive";
  const pageSize = 50;
  const { token, onUnauthorized } = useOutletContext<AdminConsoleContext>();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>("all");
  const [stores, setStores] = useState<AdminStoreSummary[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [installMappings, setInstallMappings] = useState<AdminShopifyAppInstallMapping[]>([]);
  const [installMappingForm, setInstallMappingForm] = useState<AdminShopifyAppInstallMapping>(emptyInstallMapping);
  const [savingInstallMapping, setSavingInstallMapping] = useState(false);
  const [deletingInstallMappingId, setDeletingInstallMappingId] = useState<number | null>(null);

  const loadStores = useCallback(async (pageToLoad: number, queryToLoad: string, filterToLoad: ActiveFilter) => {
    setIsLoading(true);
    setError(null);

    try {
      const isActive = filterToLoad === "all" ? undefined : filterToLoad === "active";
      const response = await agent.Admin.stores(token, queryToLoad, pageToLoad, pageSize, isActive);
      setStores(response.items);
      setTotalCount(response.totalCount);
      setPage(response.page);
    } catch {
      setError("Unable to load stores. Please login again.");
      onUnauthorized();
    } finally {
      setIsLoading(false);
    }
  }, [token, onUnauthorized, pageSize]);

  const loadInstallMappings = useCallback(async () => {
    try {
      const response = await agent.Admin.shopifyAppInstallMappings(token);
      setInstallMappings(response);
    } catch {
      setError("Unable to load Shopify app install mappings.");
    }
  }, [token]);

  useEffect(() => {
    loadStores(1, "", "all");
    loadInstallMappings();
  }, [loadInstallMappings, loadStores]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalCount / pageSize)), [totalCount, pageSize]);

  const handleSearch = useCallback(() => {
    void loadStores(1, query.trim(), activeFilter);
  }, [activeFilter, loadStores, query]);

  const handleRefresh = useCallback(() => {
    void loadStores(page, query.trim(), activeFilter);
  }, [activeFilter, loadStores, page, query]);

  const handleNextPage = useCallback(() => {
    if (page >= totalPages) return;
    void loadStores(page + 1, query.trim(), activeFilter);
  }, [activeFilter, loadStores, page, query, totalPages]);

  const handlePreviousPage = useCallback(() => {
    if (page <= 1) return;
    void loadStores(page - 1, query.trim(), activeFilter);
  }, [activeFilter, loadStores, page, query]);

  const handleInstallMappingChange = <K extends keyof AdminShopifyAppInstallMapping>(key: K, value: AdminShopifyAppInstallMapping[K]) => {
    setInstallMappingForm((current) => ({ ...current, [key]: value }));
  };

  const handleEditInstallMapping = (mapping: AdminShopifyAppInstallMapping) => {
    setInstallMappingForm({
      ...mapping,
      secretKey: "",
    });
  };

  const handleSaveInstallMapping = async () => {
    setSavingInstallMapping(true);
    setError(null);

    try {
      await agent.Admin.upsertShopifyAppInstallMapping(token, installMappingForm);
      setInstallMappingForm(emptyInstallMapping);
      await loadInstallMappings();
    } catch {
      setError("Unable to save Shopify app install mapping.");
    } finally {
      setSavingInstallMapping(false);
    }
  };

  const handleDeleteInstallMapping = async (mapping: AdminShopifyAppInstallMapping) => {
    if (!mapping.id) return;

    const confirmed = window.confirm(`Delete install mapping for ${mapping.shopDomain}?`);
    if (!confirmed) return;

    setDeletingInstallMappingId(mapping.id);
    setError(null);

    try {
      await agent.Admin.deleteShopifyAppInstallMapping(token, mapping.id);
      await loadInstallMappings();
    } catch {
      setError("Unable to delete Shopify app install mapping.");
    } finally {
      setDeletingInstallMappingId(null);
    }
  };

  return (
    <BlockStack gap="400">
      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h2" variant="headingMd">Pre-install Shopify app mappings</Text>
            <Button onClick={loadInstallMappings}>Refresh mappings</Button>
          </InlineStack>
          <InlineStack gap="300" align="start">
            <div style={{ minWidth: "260px", flex: 1 }}>
              <TextField
                label="Shop domain"
                autoComplete="off"
                placeholder="example.myshopify.com"
                value={installMappingForm.shopDomain}
                onChange={(value) => handleInstallMappingChange("shopDomain", value)}
                disabled={savingInstallMapping}
              />
            </div>
            <div style={{ minWidth: "220px", flex: 1 }}>
              <TextField
                label="App name"
                autoComplete="off"
                value={installMappingForm.appName || ""}
                onChange={(value) => handleInstallMappingChange("appName", value)}
                disabled={savingInstallMapping}
              />
            </div>
            <div style={{ minWidth: "220px", flex: 1 }}>
              <TextField
                label="App handle"
                autoComplete="off"
                value={installMappingForm.appHandle || ""}
                onChange={(value) => handleInstallMappingChange("appHandle", value)}
                disabled={savingInstallMapping}
              />
            </div>
          </InlineStack>
          <InlineStack gap="300" align="start">
            <div style={{ minWidth: "320px", flex: 1 }}>
              <TextField
                label="Client ID / API key"
                autoComplete="off"
                value={installMappingForm.clientId}
                onChange={(value) => handleInstallMappingChange("clientId", value)}
                disabled={savingInstallMapping}
              />
            </div>
            <div style={{ minWidth: "320px", flex: 1 }}>
              <TextField
                label="API secret key"
                type="password"
                autoComplete="off"
                value={installMappingForm.secretKey || ""}
                onChange={(value) => handleInstallMappingChange("secretKey", value)}
                disabled={savingInstallMapping}
                helpText={installMappingForm.hasSecretKey ? "A secret is saved. Leave blank to keep it." : undefined}
              />
            </div>
          </InlineStack>
          <InlineStack gap="200" align="end">
            <Button onClick={() => setInstallMappingForm(emptyInstallMapping)} disabled={savingInstallMapping}>Clear form</Button>
            <Button variant="primary" onClick={handleSaveInstallMapping} loading={savingInstallMapping} disabled={savingInstallMapping}>
              Save install mapping
            </Button>
          </InlineStack>
          {installMappings.length === 0 ? (
            <Text as="p" tone="subdued">No pre-install mappings saved.</Text>
          ) : (
            <IndexTable
              resourceName={{ singular: "mapping", plural: "mappings" }}
              itemCount={installMappings.length}
              selectable={false}
              headings={[
                { title: "Shop domain" },
                { title: "App" },
                { title: "Client ID" },
                { title: "Secret" },
                { title: "Updated" },
                { title: "Actions" },
              ]}
            >
              {installMappings.map((mapping, index) => (
                <IndexTable.Row id={`${mapping.id}`} key={mapping.id || mapping.shopDomain} position={index}>
                  <IndexTable.Cell>{mapping.shopDomain}</IndexTable.Cell>
                  <IndexTable.Cell>{mapping.appName || mapping.appHandle || "-"}</IndexTable.Cell>
                  <IndexTable.Cell>{mapping.clientId}</IndexTable.Cell>
                  <IndexTable.Cell>{mapping.hasSecretKey ? <Badge tone="success">Saved</Badge> : <Badge tone="critical">Missing</Badge>}</IndexTable.Cell>
                  <IndexTable.Cell>{formatLocalDateTime(mapping.updatedAt || mapping.createdAt)}</IndexTable.Cell>
                  <IndexTable.Cell>
                    <InlineStack gap="200">
                      <Button variant="plain" onClick={() => handleEditInstallMapping(mapping)}>Edit</Button>
                      <Button
                        variant="plain"
                        tone="critical"
                        loading={deletingInstallMappingId === mapping.id}
                        disabled={deletingInstallMappingId !== null}
                        onClick={() => handleDeleteInstallMapping(mapping)}
                      >
                        Delete
                      </Button>
                    </InlineStack>
                  </IndexTable.Cell>
                </IndexTable.Row>
              ))}
            </IndexTable>
          )}
        </BlockStack>
      </Card>

      <Card>
      <div style={{ display: "grid", gap: "0.75rem" }}>
        <TextField
          label="Search by domain, myshopify domain, shop name, or email"
          value={query}
          onChange={setQuery}
          onBlur={handleSearch}
          autoComplete="off"
          placeholder="example.myshopify.com"
        />
        <Select
          label="Status"
          options={[
            { label: "All stores", value: "all" },
            { label: "Active only", value: "active" },
            { label: "Inactive only", value: "inactive" }
          ]}
          value={activeFilter}
          onChange={(value) => {
            const nextFilter = value as ActiveFilter;
            setActiveFilter(nextFilter);
            void loadStores(1, query.trim(), nextFilter);
          }}
        />
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <Button onClick={handleSearch} loading={isLoading}>Search</Button>
          <Button onClick={() => { setQuery(""); setActiveFilter("all"); void loadStores(1, "", "all"); }} disabled={isLoading}>Clear</Button>
        </div>
        {error ? <Text as="p" tone="critical">{error}</Text> : null}
        {stores.length === 0 ? (
          <Text as="p" variant="bodyMd">No stores found.</Text>
        ) : (
          <IndexTable
            resourceName={{ singular: "store", plural: "stores" }}
            itemCount={stores.length}
            selectable={false}
            headings={[
              { title: "Name" },
              { title: "Domain" },
              { title: "Email" },
              { title: "Is Active" },
              { title: "Last Sync" },
              { title: "Actions" },
            ]}
          >
            {stores.map((store, index) => (
              <IndexTable.Row id={`${store.shopId}`} key={store.shopId} position={index}>
                <IndexTable.Cell>
                  <Text as="span" fontWeight="semibold">{store.name || store.domain || `Shop ${store.shopId}`}</Text>
                </IndexTable.Cell>
                <IndexTable.Cell>{store.domain || store.myShopifyDomain || "-"}</IndexTable.Cell>
                <IndexTable.Cell>{store.email || store.contactEmail || "-"}</IndexTable.Cell>
                <IndexTable.Cell>{store.isActive ? <Badge tone="success">Active</Badge> : <Badge tone="warning">Inactive</Badge>}</IndexTable.Cell>
                <IndexTable.Cell>{formatLocalDateTime(store.lastSyncAt)}</IndexTable.Cell>
                <IndexTable.Cell>
                  <Button variant="plain" onClick={() => navigate(`/pf-console/stores/${store.shopId}`)}>View details</Button>
                </IndexTable.Cell>
              </IndexTable.Row>
            ))}
          </IndexTable>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Text as="p" variant="bodyMd">Page {page} of {totalPages} • {totalCount} stores total</Text>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <Button onClick={handlePreviousPage} disabled={isLoading || page <= 1}>Previous</Button>
            <Button onClick={handleNextPage} disabled={isLoading || page >= totalPages}>Next</Button>
            <Button onClick={handleRefresh} loading={isLoading}>Refresh stores</Button>
          </div>
        </div>
      </div>
      </Card>
    </BlockStack>
  );
}
